import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { format, addDays, startOfDay, endOfMonth } from 'date-fns';

const ALL_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
  '16:00', '17:00', '18:00', '19:00', '20:00',
];

// Build hourly slot start times from an open/close window (inclusive of close).
// e.g. 09:00–20:00 -> ['09:00', ..., '20:00']. Falls back to the fixed list.
function slotsFromWindow(openTime, closeTime) {
  if (!openTime || !closeTime) return ALL_SLOTS;
  const [oh] = openTime.split(':').map(Number);
  const [ch] = closeTime.split(':').map(Number);
  if (Number.isNaN(oh) || Number.isNaN(ch) || ch < oh) return ALL_SLOTS;
  const out = [];
  for (let h = oh; h <= ch; h++) out.push(`${String(h).padStart(2, '0')}:00`);
  return out.length ? out : ALL_SLOTS;
}

/**
 * Fetches all confirmed + pending bookings for a date range (via the
 * getBookingAvailability backend function, which bypasses Booking RLS so
 * the public booking flow sees real availability), the venue's
 * concurrent_capacity, and the experience opening-hours schedule.
 *
 * The experience schedule drives the slot list (open_time → close_time) and
 * which days are bookable (open_days). Defaults replicate the previous
 * hardcoded 09:00–20:00, all-days behaviour so nothing breaks before an
 * admin configures the Opening Hours page.
 */
export function useBookingAvailability(visibleMonth) {
  const rangeStart = startOfDay(new Date());
  const defaultEnd = addDays(rangeStart, 90);
  const monthEnd = visibleMonth ? endOfMonth(visibleMonth) : null;
  const rangeEnd = (monthEnd && monthEnd > defaultEnd) ? monthEnd : defaultEnd;

  const { data = { bookings: [], concurrent_capacity: 1, experience_hours: null, bookings_paused: false } } = useQuery({
    queryKey: ['booking-availability', format(rangeStart, 'yyyy-MM-dd'), format(rangeEnd, 'yyyy-MM-dd')],
    queryFn: async () => {
      const res = await base44.functions.invoke('getBookingAvailability', {
        startDate: format(rangeStart, 'yyyy-MM-dd'),
        endDate: format(rangeEnd, 'yyyy-MM-dd'),
      });
      return res.data;
    },
    staleTime: 1000 * 30, // 30s — availability changes as people book
  });

  const capacity = data.concurrent_capacity || 1;
  const bookings = data.bookings || [];
  const bookingsPaused = !!data.bookings_paused;
  const experienceHours = data.experience_hours || {};
  const slots = useMemo(
    () => slotsFromWindow(experienceHours.open_time, experienceHours.close_time),
    [experienceHours.open_time, experienceHours.close_time],
  );
  const openDays = useMemo(
    () => (Array.isArray(experienceHours.open_days) && experienceHours.open_days.length
      ? experienceHours.open_days
      : [0, 1, 2, 3, 4, 5, 6]),
    [experienceHours.open_days],
  );

  // date → rig_type → slot → count
  const byDateRigSlot = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.date || !b.time_slot || !b.rig_type) continue;
      if (!map[b.date]) map[b.date] = {};
      if (!map[b.date][b.rig_type]) map[b.date][b.rig_type] = {};
      map[b.date][b.rig_type][b.time_slot] = (map[b.date][b.rig_type][b.time_slot] || 0) + 1;
    }
    return map;
  }, [bookings]);

  // Remaining for a specific rig on a specific date+slot.
  // Each rig seats 1 person → remaining = 1 − bookings on that rig+slot.
  // When bookings are paused, every slot shows 0 remaining (waitlist only).
  const remainingForRig = useCallback((dateStr, rigKey, slot) => {
    if (bookingsPaused) return 0;
    const count = byDateRigSlot[dateStr]?.[rigKey]?.[slot] || 0;
    return Math.max(0, 1 - count);
  }, [byDateRigSlot, bookingsPaused]);

  // Per-slot remaining map for a date + rig (for the time grid).
  const slotRemainingForRig = useCallback((dateStr, rigKey) => {
    const result = {};
    for (const slot of slots) {
      result[slot] = remainingForRig(dateStr, rigKey, slot);
    }
    return result;
  }, [remainingForRig, slots, bookingsPaused]);

  // Day hint for the calendar: 'open' | 'limited' | 'full' | 'closed'.
  // 'closed' = the venue isn't open on this day per the experience schedule.
  const dayHint = useCallback((dateStr) => {
    const dow = new Date(dateStr + 'T00:00:00').getDay();
    if (!openDays.includes(dow)) return 'closed';
    if (bookingsPaused) return 'full';
    const dateMap = byDateRigSlot[dateStr];
    if (!dateMap) return 'open';
    let total = 0;
    for (const rigKey of Object.keys(dateMap)) {
      for (const slot of Object.keys(dateMap[rigKey])) {
        total += dateMap[rigKey][slot];
      }
    }
    const maxPossible = capacity * slots.length;
    const remaining = Math.max(0, maxPossible - total);
    if (remaining === 0) return 'full';
    if (remaining <= maxPossible * 0.25) return 'limited';
    return 'open';
  }, [byDateRigSlot, capacity, slots, openDays, bookingsPaused]);

  // Next available date+slot for a specific rig (for the rig picker chip).
  // Scans up to 60 days from today, skipping closed days and past slots on today.
  const nextAvailableForRig = useCallback((rigKey) => {
    if (bookingsPaused) return null;
    const from = startOfDay(new Date());
    const nowHour = new Date().getHours();
    for (let i = 0; i < 60; i++) {
      const d = addDays(from, i);
      const dateStr = format(d, 'yyyy-MM-dd');
      const dow = d.getDay();
      if (!openDays.includes(dow)) continue;
      const remaining = slotRemainingForRig(dateStr, rigKey);
      const available = slots.filter((s) => {
        if (remaining[s] <= 0) return false;
        if (i === 0) {
          const slotHour = parseInt(s.split(':')[0], 10);
          if (slotHour <= nowHour) return false;
        }
        return true;
      });
      if (available.length > 0) {
        return { date: dateStr, slot: available[0] };
      }
    }
    return null;
  }, [slotRemainingForRig, slots, openDays, bookingsPaused]);

  return {
    capacity,
    bookings,
    bookingsPaused,
    slots,
    openDays,
    remainingForRig,
    slotRemainingForRig,
    dayHint,
    nextAvailableForRig,
  };
}